'use client';

import { Captions, Plus, Trash2 } from 'lucide-react';
import { useI18n } from '@/contexts/I18nContext';
import { useEditorStore } from '@/lib/editor/editorStore';
import { cn } from '@/lib/utils/cn';

/**
 * Left "Субтитры" tool panel (editor V2 rewrite).
 *
 * A caption is deliberately NOT a new render primitive — it's a normal
 * `type: 'text'` overlay layer tagged `role: 'caption'` (see
 * `addCaptionLayer` in `editorStore.ts`). The existing preview render and
 * server render (`render-worker.ts`) already draw any text layer, so this
 * panel only needs list/add/edit/select/delete affordances; per-layer
 * position/style/timing editing reuses the existing right-side "Детали"
 * panel (Параметры / Стиль / Тайминг tabs) once a caption is selected.
 */
function formatTimecode(seconds: number) {
  if (!Number.isFinite(seconds) || seconds <= 0) return '00:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
}

export function CaptionsPanel() {
  const { t } = useI18n();
  const layers = useEditorStore((s) => s.layers);
  const selectedLayerId = useEditorStore((s) => s.selectedLayerId);
  const addCaptionLayer = useEditorStore((s) => s.addCaptionLayer);
  const updateLayer = useEditorStore((s) => s.updateLayer);
  const selectLayer = useEditorStore((s) => s.selectLayer);
  const removeLayer = useEditorStore((s) => s.removeLayer);

  const captions = layers.filter((l) => l.role === 'caption');

  return (
    <div className="flex h-full min-h-0 flex-col bg-[#121214] text-white">
      <div className="flex h-10 shrink-0 items-center border-b border-white/10 px-3.5">
        <p className="text-sm font-semibold">{t('studio.editor.toolCaptions')}</p>
      </div>

      <div data-lenis-prevent className="min-h-0 flex-1 overflow-y-auto p-3">
        <p className="text-xs leading-5 text-white/60">
          Добавьте субтитры поверх видео — таймингом и позицией можно управлять справа, во вкладке
          «Тайминг».
        </p>

        <button
          type="button"
          onClick={addCaptionLayer}
          className="mt-3 inline-flex h-9 w-full items-center justify-center gap-2 rounded-xl bg-brand-600 text-xs font-black text-white shadow-[0_16px_34px_rgba(91,53,255,0.2)] transition hover:bg-brand-500"
        >
          <Plus className="h-3.5 w-3.5" />
          Добавить субтитр
        </button>

        <p className="mt-5 text-[11px] font-semibold uppercase tracking-wide text-white/40">
          Субтитры {captions.length > 0 ? `(${captions.length})` : ''}
        </p>

        {captions.length ? (
          <div className="mt-2 space-y-1.5">
            {captions.map((caption) => {
              const selected = selectedLayerId === caption.id;
              return (
                <div
                  key={caption.id}
                  className={cn(
                    'rounded-lg border px-2.5 py-2 transition',
                    selected ? 'border-brand-500 bg-brand-500/15' : 'border-white/10 bg-white/[0.02] hover:border-white/20'
                  )}
                >
                  <div className="flex items-center justify-between gap-2">
                    <button
                      type="button"
                      onClick={() => selectLayer(caption.id)}
                      className="min-w-0 flex-1 truncate text-left text-[10px] font-medium tabular-nums text-white/40 hover:text-white/70"
                    >
                      {formatTimecode(caption.start)}–{formatTimecode(caption.end)}
                    </button>
                    <button
                      type="button"
                      onClick={() => removeLayer(caption.id)}
                      aria-label="Удалить субтитр"
                      className="shrink-0 rounded-full p-1 text-white/40 transition hover:bg-red-500/10 hover:text-red-400"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <input
                    type="text"
                    value={caption.text}
                    onChange={(e) => updateLayer(caption.id, { text: e.target.value })}
                    onFocus={() => selectLayer(caption.id)}
                    placeholder="Текст субтитра"
                    className="mt-1.5 w-full rounded-lg border border-white/10 bg-white/[0.03] px-2 py-1.5 text-xs text-white placeholder:text-white/30 focus:border-brand-500 focus:outline-none"
                  />
                </div>
              );
            })}
          </div>
        ) : (
          <div className="mt-2 flex flex-col items-center gap-2 rounded-xl border border-dashed border-white/15 px-3 py-6 text-center">
            <Captions className="h-5 w-5 text-white/30" />
            <p className="text-xs leading-5 text-white/50">
              Субтитров пока нет. Нажмите «Добавить субтитр», чтобы создать первый.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
